"use client";

import type { CredentialsMetaResponse } from "@/app/api/__generated__/models/credentialsMetaResponse";
import { Button } from "@/components/atoms/Button/Button";
import { Input } from "@/components/atoms/Input/Input";
import { Switch } from "@/components/atoms/Switch/Switch";
import { Text } from "@/components/atoms/Text/Text";
import { MCPAuthSchemeField } from "@/components/contextual/MCPAuthSchemeField/MCPAuthSchemeField";
import {
  mcpAuthTokenHint,
  mcpAuthTokenLabel,
  mcpAuthTokenPlaceholder,
} from "@/components/contextual/MCPAuthSchemeField/helpers";
import { useMCPConnectPanel } from "./useMCPConnectPanel";
import type { MCPAuthScheme } from "@/lib/mcp-auth";
import { MCPServerURLField } from "./MCPServerURLField";
import { noPasswordManager } from "./helpers";
import type { ReactNode } from "react";

interface Props {
  onSuccess: (credential?: CredentialsMetaResponse) => void;
  initialServerURL?: string;
  lockServerURL?: boolean;
  allowedAuthMethods?: ("oauth" | MCPAuthScheme)[];
  oauthScopes?: string[] | null;
  oauthWriteScopes?: string[];
  serverURLOptions?: { label: string; url: string }[];
  /** Extra buttons for the row that holds Connect. */
  actions?: ReactNode;
}

export function McpConnectPanel({
  onSuccess,
  initialServerURL = "",
  lockServerURL = false,
  allowedAuthMethods,
  oauthScopes,
  oauthWriteScopes = [],
  serverURLOptions,
  actions,
}: Props) {
  const state = useMCPConnectPanel({
    onSuccess,
    initialServerURL,
    allowedAuthMethods,
    oauthScopes,
    oauthWriteScopes,
  });

  return (
    <div className="flex flex-col gap-4" data-sentry-component="McpConnectPanel" data-sentry-source-file="McpConnectPanel.tsx">
      {!lockServerURL && (
        <Text variant="body" tone="secondary">
          Enter the server URL from the service&apos;s setup instructions.
        </Text>
      )}
      <MCPServerURLField
        serverURL={state.serverURL}
        onChange={state.handleServerURLChange}
        disabled={state.isSubmitting}
        readOnly={lockServerURL}
        options={serverURLOptions}
      data-sentry-element="MCPServerURLField" data-sentry-source-file="McpConnectPanel.tsx" />
      {state.phase === "form" && oauthWriteScopes.length > 0 && (
        <label className="flex cursor-pointer items-start gap-3">
          <Switch
            checked={state.allowChanges}
            onCheckedChange={state.setAllowChanges}
            disabled={state.isSubmitting}
            className="mt-0.5"
          />
          <span className="flex flex-col">
            <Text variant="body-medium" as="span">
              Allow changes
            </Text>
            <Text variant="small" as="span" tone="muted">
              Without this it can only read, not create or edit.
            </Text>
          </span>
        </label>
      )}
      {state.phase === "manual-token" && (
        <>
          <Text variant="small" tone="secondary">
            {allowedAuthMethods
              ? "Use the credential described in the setup instructions above."
              : "Use an API credential only if this server supports it. Follow the server's documentation for the correct authentication type."}
          </Text>
          {state.manualSchemes.length > 1 && (
            <MCPAuthSchemeField
              value={state.authScheme}
              onChange={state.selectScheme}
              disabled={state.isSubmitting}
              className="flex flex-col gap-1"
              labelClassName="text-body font-medium text-foreground-body"
              selectClassName="rounded-lg border border-border-strong bg-surface px-3 py-2 text-foreground-strong"
            />
          )}
          <Input
            id="mcp-auth-token"
            label={mcpAuthTokenLabel(state.authScheme)}
            type="password"
            placeholder={mcpAuthTokenPlaceholder(state.authScheme)}
            value={state.token}
            onChange={(e) => state.handleTokenChange(e.target.value)}
            disabled={state.isSubmitting}
            hint={mcpAuthTokenHint(state.authScheme)}
            {...noPasswordManager}
            autoComplete="new-password"
          />
        </>
      )}
      {state.error && (
        <div
          role="alert"
          aria-live="polite"
          className="rounded-lg border border-error/20 bg-error-surface px-3 py-2 text-body text-error-foreground"
        >
          {state.error}
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        {actions}
        {state.phase === "form" && state.manualSchemes.length > 0 && (
          <Button
            variant="secondary"
            size="small"
            onClick={state.handleSwitchToToken}
          >
            Use an API token instead
          </Button>
        )}
        {state.phase === "manual-token" && state.canUseOAuth && (
          <Button
            variant="secondary"
            size="small"
            onClick={state.handleSwitchToOAuth}
            disabled={state.isSubmitting}
          >
            Try OAuth
          </Button>
        )}
        <Button
          variant="primary"
          size="small"
          onClick={
            state.phase === "form"
              ? state.handleConnect
              : state.handleSubmitToken
          }
          disabled={
            state.phase === "form" ? !state.canConnect : !state.canSubmitToken
          }
          loading={state.isSubmitting}
        data-sentry-element="Button" data-sentry-source-file="McpConnectPanel.tsx">
          {state.phase === "form" ? "Connect" : "Save token"}
        </Button>
      </div>
    </div>
  );
}
